import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Layout from '@/components/Layout';
import PlayAnimation from '@/components/PlayAnimation';
import { useLang } from '@/context/LangContext';
import { api } from '@/lib/api';
import { Brain, Trophy, Target, ChevronRight, Loader2, Lock, CheckCircle2, XCircle, Clock, ArrowLeft, Volume2, VolumeX, Crown, Medal, Plus, Share2, Copy } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import CreateChallengeModal from '@/components/CreateChallengeModal';
import { buildIQScenarioSteps } from '@/lib/iqScenarioAnimation';

/**
 * Game IQ Simulator — Phase 2.
 *
 * Phase 1 stayed: hub + scenario + result.
 * Phase 2 adds:
 *   - 'complete' category (rich game state — scoreboard, fouls, clock).
 *   - Leaderboard tab with global ranking + your own row.
 *   - Browser TTS narration of the situation (toggleable).
 */

const LANG_TO_BCP47 = { es: 'es-ES', en: 'en-US', fr: 'fr-FR' };

export default function IQSimulator() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const isCoach = user?.role === 'coach' || user?.role === 'admin';
  const [view, setView] = useState('hub'); // hub | scenario | result
  const [scenarios, setScenarios] = useState([]);
  const [score, setScore] = useState(null);
  const [planInfo, setPlanInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [reactionStart, setReactionStart] = useState(null);
  const [tab, setTab] = useState('offensive'); // offensive | defensive | complete | leaderboard
  const [leaderboard, setLeaderboard] = useState(null);
  const [leaderboardCat, setLeaderboardCat] = useState('');
  const [ttsEnabled, setTtsEnabled] = useState(() => {
    try { return localStorage.getItem('iq_tts') === '1'; } catch { return false; }
  });
  const [myChallenges, setMyChallenges] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    const langParam = `?lang=${encodeURIComponent(lang || 'es')}`;
    Promise.all([
      api.get(`/iq/scenarios${langParam}`).then(r => r.data.scenarios || []).catch(() => []),
      api.get('/iq/score').then(r => r.data).catch(() => null),
      api.get('/payments/me').then(r => r.data).catch(() => null),
    ]).then(([list, sc, plan]) => {
      if (!mounted) return;
      setScenarios(list);
      setScore(sc);
      setPlanInfo(plan);
    }).finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [lang]);

  const refreshMyChallenges = useCallback(() => {
    if (!isCoach) return;
    api.get('/iq/challenges/mine')
      .then(r => setMyChallenges(r.data.challenges || []))
      .catch(() => setMyChallenges([]));
  }, [isCoach]);

  useEffect(() => { refreshMyChallenges(); }, [refreshMyChallenges]);

  // Stop any in-flight TTS narration when the user leaves a scenario screen.
  useEffect(() => {
    if (view !== 'scenario') {
      try { window.speechSynthesis?.cancel(); } catch {/* noop */}
    }
  }, [view]);

  const isPremium = !!planInfo?.is_premium;

  const filtered = useMemo(
    () => scenarios.filter(s => s.category === tab),
    [scenarios, tab]
  );

  const fetchLeaderboard = useCallback(async (cat = '') => {
    try {
      const url = cat ? `/iq/leaderboard?category=${cat}` : '/iq/leaderboard';
      const { data } = await api.get(url);
      setLeaderboard(data);
    } catch {
      setLeaderboard({ top: [], me: null, category: cat || null });
    }
  }, []);

  useEffect(() => {
    if (tab === 'leaderboard') {
      fetchLeaderboard(leaderboardCat);
    }
  }, [tab, leaderboardCat, fetchLeaderboard]);

  // ---------------- TTS ----------------
  const speak = useCallback((text) => {
    if (!ttsEnabled || !text || typeof window === 'undefined' || !window.speechSynthesis) return;
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.lang = LANG_TO_BCP47[lang] || 'es-ES';
      u.rate = 1.0;
      u.pitch = 1.0;
      window.speechSynthesis.speak(u);
    } catch {/* noop */}
  }, [ttsEnabled, lang]);

  const toggleTts = () => {
    setTtsEnabled(prev => {
      const next = !prev;
      try { localStorage.setItem('iq_tts', next ? '1' : '0'); } catch {/* noop */}
      if (!next) { try { window.speechSynthesis?.cancel(); } catch {/* noop */} }
      return next;
    });
  };

  const openScenario = async (sc) => {
    if (!isPremium) {
      toast.error(t('iq_premium_required'));
      return;
    }
    try {
      const { data } = await api.get(`/iq/scenarios/${sc.id}?lang=${encodeURIComponent(lang || 'es')}`);
      setActive(data);
      setResult(null);
      setReactionStart(Date.now());
      setView('scenario');
      // Narrate the situation right after the scenario is rendered.
      setTimeout(() => speak(data.situation), 400);
    } catch (err) {
      if (err?.response?.status === 402) {
        toast.error(t('iq_premium_required'));
      } else {
        toast.error(err?.message || 'Error');
      }
    }
  };

  const decide = async (optionId) => {
    if (!active || submitting) return;
    setSubmitting(true);
    try { window.speechSynthesis?.cancel(); } catch {/* noop */}
    const reaction = reactionStart ? (Date.now() - reactionStart) / 1000 : 0;
    try {
      const { data } = await api.post(`/iq/scenarios/${active.id}/decide?lang=${encodeURIComponent(lang || 'es')}`, {
        option_id: optionId,
        reaction_seconds: reaction,
      });
      setResult({ ...data, chosen_option_id: optionId });
      setView('result');
      api.get('/iq/score').then(r => setScore(r.data)).catch(() => {});
    } catch (err) {
      if (err?.response?.status === 402) {
        toast.error(t('iq_premium_required'));
      } else {
        toast.error(err?.response?.data?.detail || err?.message || 'Error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const backToHub = () => {
    setView('hub');
    setActive(null);
    setResult(null);
    setReactionStart(null);
  };

  const tryAnother = () => {
    setActive(null);
    setResult(null);
    setReactionStart(null);
    setView('hub');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      </Layout>
    );
  }

  // ---------------- SCENARIO ----------------
  if (view === 'scenario' && active) {
    const gs = active.game_state;
    return (
      <Layout>
        <div className="max-w-4xl mx-auto" data-testid="iq-scenario-view">
          <div className="flex items-center justify-between gap-3 mb-4">
            <button
              onClick={backToHub}
              data-testid="iq-back-hub"
              className="inline-flex items-center gap-1.5 text-gold/80 hover:text-gold text-sm"
            >
              <ArrowLeft className="w-4 h-4" /> {t('iq_back_to_hub')}
            </button>
            <button
              onClick={toggleTts}
              data-testid="iq-tts-toggle"
              title={ttsEnabled ? t('iq_tts_off') : t('iq_tts_on')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-h uppercase tracking-wider border ${
                ttsEnabled ? 'border-gold text-gold bg-gold/10' : 'border-gray-600 text-gray-400 hover:border-gold/60 hover:text-gold'
              }`}
            >
              {ttsEnabled ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
              {ttsEnabled ? t('iq_tts_label_on') : t('iq_tts_label_off')}
            </button>
          </div>

          <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-1">
            {active.category === 'offensive' ? t('iq_cat_offensive')
              : active.category === 'defensive' ? t('iq_cat_defensive')
              : t('iq_cat_complete')}
            <span className="text-gold/50"> · {active.difficulty}</span>
          </div>
          <h1 className="font-h uppercase text-3xl sm:text-4xl mb-3">{active.title}</h1>

          {/* Game state scoreboard — only for "complete" scenarios */}
          {gs && (
            <div
              className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-5 rounded-lg border-2 border-gold/40 bg-navy-deep p-3"
              data-testid="iq-game-state"
            >
              <ScoreCell label={t('iq_gs_quarter')} value={gs.quarter} />
              <ScoreCell label={t('iq_gs_clock')} value={gs.clock} accent />
              <ScoreCell label={t('iq_gs_score')} value={gs.score} />
              <ScoreCell label={t('iq_gs_fouls')} value={gs.team_fouls} />
              <ScoreCell
                label={t('iq_gs_possession')}
                value={gs.possession === 'ours' ? t('iq_gs_ours') : t('iq_gs_theirs')}
              />
              {gs.bonus && (
                <div className="col-span-2 sm:col-span-5 text-[10px] font-mono uppercase tracking-[0.25em] text-gold pt-1">
                  {t('iq_gs_bonus')}
                </div>
              )}
            </div>
          )}

          <p className="text-gray-300 mb-6 leading-relaxed">{active.situation}</p>

          <div className="rounded-lg overflow-hidden border-2 border-gold/40 bg-navy-deep mb-6">
            <PlayAnimation
              play={{
                id: 'iq-preview',
                name: active.title,
                court_mode: active.court_mode || 'half',
                steps: buildIQScenarioSteps(active.court_setup, active.situation),
              }}
              rosterMap={{}}
              courtMode={active.court_mode || 'half'}
              autoplay={true}
              narrate={ttsEnabled}
            />
          </div>

          <div className="mb-3 flex items-center justify-between">
            <div className="text-sm font-h uppercase tracking-wider text-gold">
              {t('iq_what_do_you_do')}
            </div>
            <div className="flex items-center gap-1.5 text-xs text-gold/70 font-mono">
              <Clock className="w-3.5 h-3.5" />
              {t('iq_reaction_tracking')}
            </div>
          </div>

          <div className="space-y-3" data-testid="iq-options">
            {(active.options || []).map((o, idx) => (
              <button
                key={o.id}
                onClick={() => decide(o.id)}
                disabled={submitting}
                data-testid={`iq-option-${o.id}`}
                className="w-full text-left px-5 py-4 rounded-lg border-2 border-gold/30 hover:border-gold hover:bg-gold/5 transition disabled:opacity-50"
              >
                <div className="flex items-start gap-3">
                  <div className="font-h text-gold text-lg w-7 flex-shrink-0">{String.fromCharCode(65 + idx)}</div>
                  <div className="text-white text-base">{o.text}</div>
                </div>
              </button>
            ))}
          </div>
          {submitting && (
            <div className="flex items-center justify-center gap-2 text-gold mt-6">
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('iq_evaluating')}
            </div>
          )}
        </div>
      </Layout>
    );
  }

  // ---------------- RESULT ----------------
  if (view === 'result' && result && active) {
    const Icon = result.is_optimal ? CheckCircle2 : XCircle;
    const tone = result.is_optimal ? 'text-green-400 border-green-400/60 bg-green-500/10' : 'text-red-300 border-red-400/60 bg-red-500/10';
    return (
      <Layout>
        <div className="max-w-3xl mx-auto" data-testid="iq-result-view">
          <button
            onClick={backToHub}
            data-testid="iq-back-hub"
            className="inline-flex items-center gap-1.5 text-gold/80 hover:text-gold text-sm mb-4"
          >
            <ArrowLeft className="w-4 h-4" /> {t('iq_back_to_hub')}
          </button>

          <div className={`rounded-lg border-2 p-6 mb-5 ${tone}`}>
            <div className="flex items-center gap-3 mb-2">
              <Icon className="w-8 h-8" />
              <div>
                <div className="text-xs font-mono uppercase tracking-[0.3em] opacity-70">
                  {result.is_optimal ? t('iq_optimal') : t('iq_suboptimal')}
                </div>
                <div className="font-h text-2xl">{result.outcome_short}</div>
              </div>
              <div className="ml-auto font-h text-3xl">
                +{result.score_awarded}
              </div>
            </div>
            <div className="text-sm leading-relaxed opacity-95">{result.outcome}</div>
          </div>

          {result.optimal_option && (
            <div className="rounded-lg border border-gold/40 bg-gold/5 p-4 mb-5">
              <div className="text-xs font-mono uppercase tracking-widest text-gold mb-1">
                {t('iq_optimal_was')}
              </div>
              <div className="text-white">{result.optimal_option.text}</div>
            </div>
          )}

          <div className="rounded-lg border border-gold/30 bg-card-steel p-5 mb-5">
            <div className="text-xs font-mono uppercase tracking-widest text-gold mb-2">
              {t('iq_tactical_explanation')}
            </div>
            <p className="text-gray-200 text-sm leading-relaxed mb-3">{result.explanation}</p>
            {result.team_should && (
              <>
                <div className="text-xs font-mono uppercase tracking-widest text-gold mb-2">
                  {t('iq_what_team_should')}
                </div>
                <p className="text-gray-200 text-sm leading-relaxed">{result.team_should}</p>
              </>
            )}
          </div>

          {result.ai_feedback && (
            <div className="rounded-lg border border-purple-400/40 bg-purple-500/5 p-5 mb-5">
              <div className="text-xs font-mono uppercase tracking-widest text-purple-300 mb-2 flex items-center gap-1.5">
                <Brain className="w-3.5 h-3.5" />
                {t('iq_ai_coach')}
              </div>
              <p className="text-gray-100 text-sm leading-relaxed italic">{result.ai_feedback}</p>
            </div>
          )}

          <div className="flex items-center gap-3 justify-end">
            <button
              onClick={tryAnother}
              data-testid="iq-try-another"
              className="px-5 py-2.5 rounded-md font-h uppercase tracking-wider bg-gold text-navy hover:bg-gold-light"
            >
              {t('iq_try_another')}
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  // ---------------- HUB ----------------
  return (
    <Layout>
      <div className="mb-6" data-testid="iq-hub-view">
        <Link
          to="/dashboard"
          data-testid="iq-back-dashboard"
          className="inline-flex items-center gap-1.5 text-gold/70 hover:text-gold text-xs font-mono uppercase tracking-[0.25em] mb-3"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> {t('back_to_dashboard')}
        </Link>
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-1">
          {t('iq_module_label')}
        </div>
        <h1 className="font-h uppercase text-4xl sm:text-5xl mb-1">{t('iq_h1')}</h1>
        <p className="text-gray-400 max-w-2xl">
          {t('iq_subtitle')}
        </p>
      </div>

      {!isPremium && (
        <div
          data-testid="iq-upsell"
          className="rounded-lg border-2 border-gold/60 bg-gradient-to-br from-gold/10 to-transparent p-5 mb-6 flex items-start gap-4"
        >
          <Lock className="w-6 h-6 text-gold flex-shrink-0 mt-1" />
          <div className="flex-1">
            <div className="font-h uppercase text-lg text-gold mb-1">
              {t('iq_premium_only')}
            </div>
            <p className="text-gray-300 text-sm mb-3">
              {t('iq_premium_pitch')}
            </p>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-4 py-2 bg-gold text-navy rounded-md font-h uppercase tracking-wider text-sm hover:bg-gold-light"
            >
              {t('iq_upgrade_cta')} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}

      {isPremium && score && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <StatCard icon={Trophy} label={t('iq_total_score')} value={score.total_score} accent />
          <StatCard icon={Target} label={t('iq_accuracy')} value={`${Math.round((score.accuracy || 0) * 100)}%`} />
          <StatCard icon={Brain} label={t('iq_rank')} value={score.iq_rank || 'Walk-on'} />
          <StatCard icon={Clock} label={t('iq_avg_reaction')} value={`${score.avg_reaction_seconds || 0}s`} />
        </div>
      )}

      {/* Challenge Mode CTA — premium coaches only */}
      {isPremium && isCoach && (
        <div
          className="mb-6 rounded-lg border-2 border-gold/50 bg-gradient-to-r from-gold/10 via-gold/5 to-transparent p-4 flex flex-wrap items-center gap-3"
          data-testid="iqc-cta"
        >
          <div className="flex-1 min-w-[260px]">
            <div className="font-h uppercase text-base text-gold mb-0.5 flex items-center gap-2">
              <Trophy className="w-4 h-4" /> {t('iqc_cta_h')}
            </div>
            <p className="text-xs text-gray-300">{t('iqc_cta_sub')}</p>
          </div>
          <button
            onClick={() => setCreateOpen(true)}
            data-testid="iqc-create-open"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-gold text-navy font-h uppercase tracking-wider text-sm font-semibold hover:bg-gold-light"
          >
            <Plus className="w-4 h-4" /> {t('iqc_cta_btn')}
          </button>
        </div>
      )}

      {isPremium && isCoach && myChallenges.length > 0 && (
        <div className="mb-6" data-testid="iqc-my-list">
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold/80 mb-2">
            {t('iqc_my_h')}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {myChallenges.slice(0, 4).map(c => (
              <MyChallengeCard key={c.code} c={c} t={t} />
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 mb-4 border-b border-gold/20 overflow-x-auto">
        <TabBtn active={tab === 'offensive'} onClick={() => setTab('offensive')} testid="iq-tab-offensive">
          {t('iq_cat_offensive')}
        </TabBtn>
        <TabBtn active={tab === 'defensive'} onClick={() => setTab('defensive')} testid="iq-tab-defensive">
          {t('iq_cat_defensive')}
        </TabBtn>
        <TabBtn active={tab === 'complete'} onClick={() => setTab('complete')} testid="iq-tab-complete">
          {t('iq_cat_complete')}
        </TabBtn>
        <TabBtn active={tab === 'leaderboard'} onClick={() => setTab('leaderboard')} testid="iq-tab-leaderboard">
          {t('iq_leaderboard')}
        </TabBtn>
      </div>

      {tab === 'leaderboard' ? (
        <LeaderboardView
          t={t}
          data={leaderboard}
          activeCat={leaderboardCat}
          onCat={setLeaderboardCat}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="iq-scenario-list">
          {filtered.map(sc => (
            <button
              key={sc.id}
              onClick={() => openScenario(sc)}
              data-testid={`iq-scenario-${sc.id}`}
              className="text-left bg-card-steel border-2 border-gold/25 hover:border-gold rounded-lg p-5 transition relative"
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="text-xs font-mono uppercase tracking-widest text-gold/80">
                  {sc.difficulty}
                </div>
                {!isPremium && <Lock className="w-4 h-4 text-gold/60" />}
              </div>
              <div className="font-h uppercase text-xl text-white mb-2">{sc.title}</div>
              {/* Mini scoreboard badge for complete scenarios */}
              {sc.game_state && (
                <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`iq-card-gs-${sc.id}`}>
                  <MiniBadge>{sc.game_state.quarter}</MiniBadge>
                  <MiniBadge accent>{sc.game_state.clock}</MiniBadge>
                  <MiniBadge>{sc.game_state.score}</MiniBadge>
                </div>
              )}
              <div className="text-sm text-gray-400 line-clamp-3">{sc.situation}</div>
              <div className="mt-3 inline-flex items-center gap-1.5 text-gold text-xs font-mono uppercase tracking-widest">
                {t('iq_open_scenario')} <ChevronRight className="w-3.5 h-3.5" />
              </div>
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-center text-gray-500 py-10">
              {t('iq_no_scenarios')}
            </div>
          )}
        </div>
      )}

      {/* Create Challenge modal — mounted at the page root so the backdrop
          covers the whole viewport regardless of scroll. */}
      <CreateChallengeModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => refreshMyChallenges()}
      />
    </Layout>
  );
}

function StatCard({ icon: Icon, label, value, accent = false }) {
  return (
    <div className={`rounded-lg border-2 p-4 ${accent ? 'border-gold bg-gold/5' : 'border-gold/25 bg-card-steel'}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${accent ? 'text-gold' : 'text-gold/70'}`} />
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-gray-400">{label}</div>
      </div>
      <div className={`font-h ${accent ? 'text-3xl text-gold' : 'text-2xl text-white'}`}>{value}</div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 -mb-px border-b-2 font-h uppercase text-sm tracking-wider transition whitespace-nowrap ${
        active ? 'border-gold text-gold' : 'border-transparent text-gray-400 hover:text-gray-200'
      }`}
    >
      {children}
    </button>
  );
}

function ScoreCell({ label, value, accent = false }) {
  return (
    <div className="text-center">
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-gray-400">{label}</div>
      <div className={`font-h ${accent ? 'text-2xl text-gold' : 'text-lg text-white'}`}>{value}</div>
    </div>
  );
}

function MiniBadge({ children, accent = false }) {
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono tracking-wider ${
      accent ? 'bg-gold/20 text-gold border border-gold/40' : 'bg-navy-deep text-gray-300 border border-gold/20'
    }`}>
      {children}
    </span>
  );
}

function LeaderboardView({ t, data, activeCat, onCat }) {
  const cats = [
    { id: '', label: t('iq_lb_all') },
    { id: 'offensive', label: t('iq_cat_offensive') },
    { id: 'defensive', label: t('iq_cat_defensive') },
    { id: 'complete', label: t('iq_cat_complete') },
  ];
  return (
    <div data-testid="iq-leaderboard-view">
      <div className="flex flex-wrap gap-2 mb-4">
        {cats.map(c => (
          <button
            key={c.id || 'all'}
            onClick={() => onCat(c.id)}
            data-testid={`iq-lb-cat-${c.id || 'all'}`}
            className={`px-3 py-1.5 rounded-full text-xs font-h uppercase tracking-wider border ${
              activeCat === c.id ? 'bg-gold text-navy border-gold' : 'border-gold/30 text-gold hover:bg-gold/10'
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {!data && (
        <div className="flex items-center justify-center py-12 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      )}
      {data && data.top && data.top.length === 0 && (
        <div className="text-center text-gray-500 py-10">{t('iq_lb_empty')}</div>
      )}
      {data && data.top && data.top.length > 0 && (
        <div className="bg-card-steel border-2 border-gold/30 rounded-lg overflow-hidden" data-testid="iq-leaderboard-table">
          <div className="grid grid-cols-12 text-[10px] font-mono uppercase tracking-[0.25em] text-gold/70 px-4 py-2 border-b border-gold/20">
            <div className="col-span-1">#</div>
            <div className="col-span-6">{t('iq_lb_player')}</div>
            <div className="col-span-2 text-right">{t('iq_lb_score')}</div>
            <div className="col-span-2 text-right">{t('iq_lb_attempts')}</div>
            <div className="col-span-1 text-right">{t('iq_lb_acc')}</div>
          </div>
          {data.top.map(row => (
            <LeaderboardRow key={row.user_id} row={row} t={t} />
          ))}
          {data.me && data.me.rank > data.top.length && (
            <>
              <div className="px-4 py-1 text-center text-gold/40 text-[10px] font-mono">…</div>
              <LeaderboardRow row={data.me} t={t} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function LeaderboardRow({ row, t }) {
  const isTop3 = row.rank <= 3;
  const RankIcon = row.rank === 1 ? Crown : row.rank <= 3 ? Medal : null;
  return (
    <div
      data-testid={`iq-lb-row-${row.user_id}`}
      className={`grid grid-cols-12 items-center px-4 py-3 border-b border-gold/10 last:border-b-0 text-sm ${
        row.is_me ? 'bg-gold/10' : ''
      }`}
    >
      <div className={`col-span-1 font-h ${isTop3 ? 'text-gold' : 'text-gray-400'} flex items-center gap-1`}>
        {RankIcon && <RankIcon className="w-3.5 h-3.5" />}{row.rank}
      </div>
      <div className="col-span-6 truncate">
        <span className={row.is_me ? 'text-gold font-h uppercase' : 'text-white'}>{row.name}</span>
        {row.is_me && <span className="ml-2 text-[10px] font-mono uppercase tracking-wider text-gold/70">{t('iq_lb_you')}</span>}
        {row.team_name && <span className="ml-2 text-[10px] font-mono text-gray-500">· {row.team_name}</span>}
      </div>
      <div className="col-span-2 text-right font-h text-white">{row.total_score}</div>
      <div className="col-span-2 text-right text-gray-400 font-mono">{row.attempts}</div>
      <div className="col-span-1 text-right text-gray-400 font-mono">{Math.round((row.accuracy || 0) * 100)}%</div>
    </div>
  );
}

function MyChallengeCard({ c, t }) {
  const shareUrl = `${window.location.origin}/iq-challenge/${c.code}`;
  const copy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try { await navigator.clipboard.writeText(shareUrl); toast.success(t('ujp_link_copied')); }
    catch { toast.error(t('ujp_copy_failed')); }
  };
  const share = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (navigator.share) {
      try { await navigator.share({ title: c.title, url: shareUrl }); } catch {/* cancelled */}
    } else { copy(e); }
  };
  return (
    <Link
      to={`/iq-challenge/${c.code}`}
      data-testid={`iqc-mine-${c.code}`}
      className="bg-card-steel border border-gold/25 hover:border-gold rounded-lg p-3 flex items-center gap-3 transition"
    >
      <div className="bg-gold/15 border border-gold/40 rounded px-2 py-1 font-mono text-xs text-gold tracking-widest flex-shrink-0">
        {c.code}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-h uppercase text-sm tracking-wider text-white truncate">{c.title}</div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500">
          {c.attempts_count || 0} {t('iqc_attempts')}
        </div>
      </div>
      <button
        onClick={share}
        title={t('ujp_share')}
        className="p-1.5 rounded border border-gold/30 text-gold hover:bg-gold/10"
      >
        <Share2 className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={copy}
        title={t('ujp_copy')}
        className="p-1.5 rounded border border-gold/30 text-gold hover:bg-gold/10"
      >
        <Copy className="w-3.5 h-3.5" />
      </button>
    </Link>
  );
}
